'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export interface UserBooking {
  id?: string;
  userProfileId: string;
  serviceRequestId?: string;
  bookingNumber: string;
  serviceType: string;
  bookingDate: string;
  completionDate?: string;
  driverId?: string;
  driverName?: string;
  driverRating?: number;
  totalAmount: number;
  paymentMethod?: string;
  paymentStatus?: string;
  status: string;
  userNotes?: string;
  adminNotes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const userBookingService = {
  async getBookingsByProfileId(userProfileId: string): Promise<UserBooking[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_bookings')
        .select('*')
        .eq('user_profile_id', userProfileId)
        .order('booking_date', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return data.map((booking: any) => ({
        id: booking.id,
        userProfileId: booking.user_profile_id,
        serviceRequestId: booking.service_request_id,
        bookingNumber: booking.booking_number,
        serviceType: booking.service_type,
        bookingDate: booking.booking_date,
        completionDate: booking.completion_date,
        driverId: booking.driver_id,
        driverName: booking.driver_name,
        driverRating: booking.driver_rating,
        totalAmount: booking.total_amount,
        paymentMethod: booking.payment_method,
        paymentStatus: booking.payment_status,
        status: booking.status,
        userNotes: booking.user_notes,
        adminNotes: booking.admin_notes,
        createdAt: booking.created_at,
        updatedAt: booking.updated_at,
      }));
    } catch (error: any) {
      throw error;
    }
  },

  async getBookingById(bookingId: string): Promise<UserBooking | null> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_bookings')
        .select('*')
        .eq('id', bookingId)
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        }
        if (error.code === 'PGRST116') {
          return null;
        }
        throw new Error(error.message);
      }

      return {
        id: data.id,
        userProfileId: data.user_profile_id,
        serviceRequestId: data.service_request_id,
        bookingNumber: data.booking_number,
        serviceType: data.service_type,
        bookingDate: data.booking_date,
        completionDate: data.completion_date,
        driverId: data.driver_id,
        driverName: data.driver_name,
        driverRating: data.driver_rating,
        totalAmount: data.total_amount,
        paymentMethod: data.payment_method,
        paymentStatus: data.payment_status,
        status: data.status,
        userNotes: data.user_notes,
        adminNotes: data.admin_notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      throw error;
    }
  },
};